import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProductGroup } from '../../context/ProductGroupContext';
import { ProductGroup } from '../../models/ProductGroup';
import { Add, Category } from '@mui/icons-material';
import ProductGroupForm from './ProductGroupForm';
import ProductGroupList from './ProductGroupList';

interface ProductGroupManagementProps {
  adminMode?: boolean;
}

const ProductGroupManagement: React.FC<ProductGroupManagementProps> = ({ adminMode = true }) => {
  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ProductGroup | null>(null);

  const { getProductGroups } = useProductGroup();
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchGroups();
  }, [currentPage]);

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getProductGroups(currentPage, ITEMS_PER_PAGE);
      setGroups(result.groups);
      setTotalGroups(result.total);
      setTotalPages(result.totalPages);
    } catch (err: any) {
      setError(err.message || 'Nie udało się załadować grup produktowych');
      console.error('Error fetching product groups:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditGroup = (group: ProductGroup) => {
    setEditingGroup(group);
    setShowAddForm(true);
  };

  const handleFormClose = () => {
    setShowAddForm(false);
    setEditingGroup(null);
    fetchGroups(); // Refresh the list after editing/adding
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  };

  return (
    <motion.section
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="mb-12"
    >
      <motion.div variants={itemVariants} className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Category className="mr-2" /> Grupy produktów
        </h2>

        {adminMode && !showAddForm && (
          <motion.button
            onClick={() => setShowAddForm(true)}
            className="bg-primary text-white rounded-full px-4 py-2 flex items-center space-x-1 font-medium"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <Add fontSize="small" />
            <span>Dodaj nową grupę</span>
          </motion.button>
        )}
      </motion.div>

      <AnimatePresence mode="wait">
        {showAddForm ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ProductGroupForm 
              onClose={handleFormClose} 
              editGroup={editingGroup} 
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ProductGroupList
              groups={groups}
              isLoading={isLoading}
              error={error}
              currentPage={currentPage}
              totalPages={totalPages}
              totalGroups={totalGroups}
              onPageChange={setCurrentPage}
              onEditGroup={handleEditGroup}
              adminMode={adminMode}
              onRefresh={fetchGroups}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default ProductGroupManagement;
